import { pipe, Option, ReadonlyArray } from "fp";

import React from "react";
import styled from "styled-components";

import { Order } from "~/Order";
import { Time } from "~/Time";
import { Timeline } from "~/Timeline";

export type Row<Event extends Timeline.Event.Any> = readonly Event[];
export function Row<Event extends Timeline.Event.Any>(props: Row.Props<Event>) {
  const events = React.useCallback(
    () =>
      pipe(
        props.row,
        ReadonlyArray.filterMapWithIndex((index, event) =>
          pipe(
            event,
            Timeline.Event.clamp(props.timeline),
            Option.map((event) => (
              <Timeline.Event key={index} {...props} event={event} />
            ))
          )
        )
      ),
    [props.row.length]
  )();

  return <Row.Container timeline={props.timeline}>{events}</Row.Container>;
}

export namespace Row {
  export type Props<Event extends Timeline.Event.Any> = {
    readonly timeline: Timeline;
    readonly row: Row<Event>;
  } & Timeline.Event.Config<Event>;

  export const height = () => "4em" as const;
  export const fitsEvent =
    <Event extends Timeline.Event.Any>(event: Event) =>
    (row: Row<Event>): boolean =>
      pipe(
        ReadonlyArray.last(row),
        Option.map(Timeline.Event.fitsOnSameLine(event)),
        Option.getOrElse(() => true as boolean)
      );

  export const Container = styled.div<{ readonly timeline?: Timeline }>`
    position: relative;
    height: ${() => height()};
    overflow: hidden;
    width: 100%;
  `;
}

export type Rows<Event extends Timeline.Event.Any> = readonly Row<Event>[];
export namespace Rows {
  export const empty = () => [() => <Row.Container />];

  /** Sort `Event`s and ensure any overlaps are placed on separate `Rows`. */
  export const fromEvents = <Event extends Timeline.Event.Any>(
    config: Timeline.Event.Config<Event>,
    events: readonly Event[]
  ): Rows<Event> =>
    pipe(
      events,
      ReadonlyArray.sort<Event>(
        Order.fromCompare(
          config?.compareFn
            ? config?.compareFn
            : (a, b) => Time.Interval.compare(a, b)
        )
      ),
      (events) => (config.compareFn ? ReadonlyArray.reverse(events) : events),
      ReadonlyArray.reduce([] as Rows<Event>, (previous, event) =>
        pipe(
          previous,
          appendEvent(event),
          Option.getOrElse(() => [...previous, [event]] as Rows<Event>)
        )
      )
    );

  const appendEvent =
    <Event extends Timeline.Event.Any>(event: Event) =>
    (rows: Rows<Event>): Option<Rows<Event>> =>
      pipe(
        rows,
        ReadonlyArray.findIndex(Row.fitsEvent(event)),
        Option.chain((index) =>
          pipe(
            rows,
            ReadonlyArray.modifyAt(index, (row) => [...row, event])
          )
        )
      );
}
